import { Button } from '@propertyfinder/button';
import { DropdownSelect, useDropdownList } from '@propertyfinder/dropdown';
import { domClassMerge } from '@propertyfinder/helpers';
import { IconBin, IconEditSquare, IconThreeDotsVertical } from '@propertyfinder/icons';
import { FunctionComponent, h } from 'preact';
import { useRef, useState } from 'preact/hooks';

import { configGetLanguage } from 'common/helper/config/get-language';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { propertyGetElapsedTime } from 'common/helper/property/get-elapsed-time';
import { createAlertCardGetFilters } from 'common/module/create-alert/card/get-filters';
import { CreateAlertFrequencyItemInterface } from 'common/module/create-alert/frequency-item.interface';
import { saveSearchFrequencies } from 'common/module/save-search/frequencies';
import { MyAccountStoreService } from 'common/service/my-account-store/service';

import { CreateAlertCardPropsInterface } from './props.interface';

export const CreateAlertCardComponent: FunctionComponent<CreateAlertCardPropsInterface> = ({
  saveSearchModel,
  error,
  onCardClicked,
  onActionsClicked,
  onRenameClicked,
  onDeleteClicked,
  onFrequencyChanged,
}) => {
  const frequency: CreateAlertFrequencyItemInterface = saveSearchFrequencies.find(
    (data) => data.value === saveSearchModel.frequency
  );

  const isRtl = configGetLanguage().isRtl;
  const actionsElem = useRef<HTMLDivElement>(null);
  const frequencyElem = useRef<HTMLDivElement>(null);
  const saveSearchFrequenciesList: CreateAlertFrequencyItemInterface[] = saveSearchFrequencies;
  const [selectedFrequency, setSelectedFrequency] = useState(frequency);
  const [errorMessage, setErrorMessage] = useState<{ id: string; error: string }>(error);
  const { close, onClick, Dropdown } = useDropdownList();

  return (
    <article
      className={'create-alert-card'}
      data-testid='create-alert-card'
      onClick={(event: MouseEvent): void => {
        if (
          !actionsElem?.current?.contains(event.target as Node) &&
          !frequencyElem?.current?.contains(event.target as Node)
        ) {
          if (onCardClicked) onCardClicked();
        }
      }}
    >
      <div className='create-alert-card__title'>
        <h3 className='create-alert-card__title--text' data-testid='create-alert-card-title'>
          {saveSearchModel.name}
        </h3>
        <div className='create-alert-card__title--actions' ref={actionsElem}>
          <Button
            type={'button'}
            icon={{ component: IconThreeDotsVertical, position: 'prefix' }}
            onClick={(e: MouseEvent): void => {
              e.preventDefault();
              e.stopPropagation();
              close();
              onActionsClicked(e);
              onClick(e);
            }}
          />
          <Dropdown
            popoverClassName={'create-alert-card__actions--popover'}
            contentClassName={'create-alert-card__actions'}
            rtl={isRtl}
            horizontalPosition='right'
            elements={[
              {
                Icon: IconEditSquare,
                onClick: (): void => {
                  onRenameClicked(saveSearchModel);
                  close();
                },
                label: i18nTranslate('Rename'),
                type: 'button',
              },
              {
                Icon: IconBin,
                onClick: (): void => {
                  onDeleteClicked(saveSearchModel);
                  close();
                },
                label: i18nTranslate('Delete'),
                type: 'button',
              },
            ]}
          />
        </div>
      </div>
      {saveSearchModel.filters && (
        <div className='create-alert-card__filters'>{createAlertCardGetFilters(saveSearchModel.filters)}</div>
      )}
      <div className='create-alert-card__receive-updates' ref={frequencyElem}>
        <h4 className='create-alert-card__receive-updates--text' data-testid='create-alert-card-updates-title'>
          {i18nTranslate('CreateAlert_ReceiveUpdatesLabel')}
        </h4>
        <div>
          <DropdownSelect
            triggerClassName={domClassMerge('create-alert-card__dropdown-frequency', {
              ['create-alert-card__dropdown-frequency--error']:
                !!errorMessage.error && errorMessage.id === saveSearchModel.id,
            })}
            popoverClassName={'create-alert-card__dropdown-popover-frequency'}
            options={saveSearchFrequenciesList}
            rtl={isRtl}
            onClick={(): void => MyAccountStoreService().onFrequencyClicked()}
            onChange={(selectedItem: CreateAlertFrequencyItemInterface): void => {
              setErrorMessage({ id: '', error: '' });
              setSelectedFrequency(selectedItem);
              MyAccountStoreService().onFrequencyChanged();
              onFrequencyChanged(selectedItem.value);
            }}
            mapToLabel={(selectedItem: CreateAlertFrequencyItemInterface): string => selectedItem?.label}
            mapToValue={(selectedItem: CreateAlertFrequencyItemInterface): string => selectedItem?.value}
            value={selectedFrequency}
          />
          {errorMessage.error && errorMessage.id === saveSearchModel.id && (
            <div className='create-alert-card__error'>{errorMessage.error}</div>
          )}
        </div>
      </div>
      <div className='create-alert-card__date'>
        {`${i18nTranslate('Created')} ${propertyGetElapsedTime(saveSearchModel.created_at)}`}
      </div>
    </article>
  );
};
